import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Box,
	Card,
	CardContent,
	Typography,
	Grid,
	CircularProgress,
	Chip,
	Divider,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Tooltip,
} from '@mui/material'
import { ViewModule, ViewList } from '@mui/icons-material'
import { useAuth } from '../contexts/auth-context'
import { useDemoMode } from '../contexts/demo-mode-context'
import { useAllSensors } from '../hooks/use-all-sensors'
import { useAllCustomersSensors } from '../hooks/use-all-customers-sensors'
import { demoWaterLabelFromMap, useDemoWaterLabelBySensorId } from '../hooks/use-demo-water-labels'
import { PageContainer } from '../components/PageContainer'

export function SensorsOverview() {
	const { customerId, isAllScope } = useAuth()
	const { redactionActive } = useDemoMode()
	const navigate = useNavigate()
	const [viewMode, setViewMode] = useState('card')

	// Always call both hooks; gate fetching internally via null / enabled flag
	const { sensors, loading: singleLoading, error: singleError } = useAllSensors(isAllScope ? null : customerId)
	const { customerGroups, loading: allLoading, error: allError } = useAllCustomersSensors({ enabled: isAllScope })

	// For demo labels — only relevant in single-customer scope
	const demoWaterIndexBySensorId = useDemoWaterLabelBySensorId(sensors)
	const demoWaterLabel = (id) => demoWaterLabelFromMap(demoWaterIndexBySensorId, id)

	const loading = isAllScope ? allLoading : singleLoading
	const error = isAllScope ? allError : singleError

	const viewToggle = (
		<Box display="flex" gap={1}>
			<Tooltip title="Card View">
				<IconButton onClick={() => setViewMode('card')} color={viewMode === 'card' ? 'primary' : 'default'}>
					<ViewModule />
				</IconButton>
			</Tooltip>
			<Tooltip title="Table View">
				<IconButton onClick={() => setViewMode('table')} color={viewMode === 'table' ? 'primary' : 'default'}>
					<ViewList />
				</IconButton>
			</Tooltip>
		</Box>
	)

	if (loading) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<CircularProgress sx={{ mr: 2 }} />
				</Box>
			</PageContainer>
		)
	}

	if (error) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<Typography color="error">Error loading sensors: {error}</Typography>
				</Box>
			</PageContainer>
		)
	}

	// ── All-customers scope ───────────────────────────────────────────────────
	if (isAllScope) {
		if (customerGroups.length === 0) {
			return (
				<PageContainer>
					<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
						<Typography color="text.secondary">No sensors configured across any customer.</Typography>
					</Box>
				</PageContainer>
			)
		}

		return (
			<PageContainer>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
					<Box>
						<Typography variant="h4" fontWeight="bold" gutterBottom>
							Sensor Overview
						</Typography>
						<Typography variant="body1" color="text.secondary">
							All customers — real-time alkalinity measurements
						</Typography>
					</Box>
					{viewToggle}
				</Box>

				{customerGroups.map((group, idx) => (
					<Box key={group.customer.id} mb={5}>
						<Box display="flex" alignItems="center" gap={1.5} mb={2}>
							<Typography variant="h6" fontWeight="bold">
								{group.customer.name}
							</Typography>
							<Chip label={group.sensors.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
						</Box>
						{idx < customerGroups.length - 1 || viewMode === 'table' ? null : null}

						{viewMode === 'card' ? (
							<Grid container spacing={3}>
								{group.sensors.map((sensor) => (
									<Grid item xs={12} sm={6} md={4} key={sensor.id}>
										<SensorCard
											sensor={sensor}
											redactionActive={false}
											demoWaterLabel={() => ''}
											onViewDetails={() => {
												navigate(`/dashboard?cust=${sensor.customer_id}&cfg=${sensor.id}`)
											}}
										/>
									</Grid>
								))}
							</Grid>
						) : (
							<SensorTable
								sensors={group.sensors}
								redactionActive={false}
								demoWaterLabel={() => ''}
								onViewDetails={(configId) => {
									const sensor = group.sensors.find((s) => s.id === configId)
									navigate(`/dashboard?cust=${sensor?.customer_id}&cfg=${configId}`)
								}}
							/>
						)}

						{idx < customerGroups.length - 1 && <Divider sx={{ mt: 4 }} />}
					</Box>
				))}
			</PageContainer>
		)
	}

	// ── Single-customer scope (existing behavior) ─────────────────────────────
	if (sensors.length === 0) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<Typography color="text.secondary">No sensors configured for this account.</Typography>
				</Box>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
				<Box>
					<Typography variant="h4" fontWeight="bold" gutterBottom>
						Sensor Overview
					</Typography>
					<Typography variant="body1" color="text.secondary">
						Real-time alkalinity measurements from all your sensors
					</Typography>
				</Box>
				{viewToggle}
			</Box>

			{viewMode === 'card' ? (
				<Grid container spacing={3}>
					{sensors.map((sensor) => (
						<Grid item xs={12} sm={6} md={4} key={sensor.id}>
							<SensorCard
								sensor={sensor}
								redactionActive={redactionActive}
								demoWaterLabel={demoWaterLabel}
								onViewDetails={() => {
									navigate(`/dashboard?cfg=${sensor.id}`)
								}}
							/>
						</Grid>
					))}
				</Grid>
			) : (
				<SensorTable
					sensors={sensors}
					redactionActive={redactionActive}
					demoWaterLabel={demoWaterLabel}
					onViewDetails={(configId) => {
						navigate(`/dashboard?cfg=${configId}`)
					}}
				/>
			)}
		</PageContainer>
	)
}

function SensorTable({ sensors, onViewDetails, redactionActive, demoWaterLabel }) {
	const getStatus = (timestamp) => {
		if (!timestamp) return { label: 'No Data', color: 'default' }
		
		const age = Date.now() - new Date(timestamp).getTime()
		const minutes = age / (1000 * 60)
		
		if (minutes < 5) return { label: 'Live', color: 'success' }
		if (minutes < 60) return { label: 'Recent', color: 'info' }
		return { label: 'Stale', color: 'warning' }
	}

	const formatValue = (value) => {
		if (value == null) return '--'
		return Number(value).toFixed(1)
	}

	const formatTime = (ts) => {
		if (!ts) return 'No data'
		const date = new Date(ts)
		return date.toLocaleString()
	}

	const hasAnyPh = sensors.some(s => s.show_ph && s.latestMeasurement?.ph != null)
	const hasAnyTemperature = sensors.some(s => s.show_temperature && s.latestMeasurement?.temperature_c != null)
	const hasAnyConductivity = sensors.some(s => s.show_conductivity && s.latestMeasurement?.conductivity_mscm != null)

	return (
		<TableContainer component={Paper}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell><strong>Sensor ID</strong></TableCell>
						<TableCell><strong>Location</strong></TableCell>
						<TableCell><strong>Status</strong></TableCell>
						<TableCell align="right"><strong>Alkalinity (ppm)</strong></TableCell>
						{hasAnyPh && <TableCell align="right"><strong>pH</strong></TableCell>}
						{hasAnyTemperature && <TableCell align="right"><strong>Temperature (°C)</strong></TableCell>}
						{hasAnyConductivity && <TableCell align="right"><strong>Conductivity (mS/cm)</strong></TableCell>}
						<TableCell><strong>Last Updated</strong></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{sensors.map((sensor) => {
						const measurement = sensor.latestMeasurement
						const status = getStatus(measurement?.timestamp)
						
						return (
							<TableRow
								key={sensor.id}
								hover
								sx={{ 
									cursor: 'pointer',
									'&:hover': { backgroundColor: 'action.hover' }
								}}
								onClick={() => onViewDetails(sensor.id)}
							>
								<TableCell>
									<Typography variant="body2" fontWeight="bold">
										{sensor.sensor_id}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography variant="body2" color="text.secondary">
										{redactionActive
											? demoWaterLabel(sensor.sensor_id)
											: sensor.sensor_location || 'No location'}
									</Typography>
								</TableCell>
								<TableCell>
									<Chip 
										label={status.label} 
										color={status.color} 
										size="small"
										sx={{ px: 1 }}
									/>
								</TableCell>
								<TableCell align="right">
									<Typography variant="body2" fontWeight="bold" color="primary">
										{formatValue(measurement?.total_alkalinity_ppm_rolling_avg)}
									</Typography>
								</TableCell>
								{hasAnyPh && (
									<TableCell align="right">
										{sensor.show_ph && measurement?.ph != null ? (
											<Typography variant="body2">
												{measurement.ph.toFixed(2)}
											</Typography>
										) : (
											<Typography variant="body2" color="text.disabled">--</Typography>
										)}
									</TableCell>
								)}
								{hasAnyTemperature && (
									<TableCell align="right">
										{sensor.show_temperature && measurement?.temperature_c != null ? (
											<Typography variant="body2">
												{measurement.temperature_c.toFixed(1)}
											</Typography>
										) : (
											<Typography variant="body2" color="text.disabled">--</Typography>
										)}
									</TableCell>
								)}
								{hasAnyConductivity && (
									<TableCell align="right">
										{sensor.show_conductivity && measurement?.conductivity_mscm != null ? (
											<Typography variant="body2">
												{measurement.conductivity_mscm.toFixed(2)}
											</Typography>
										) : (
											<Typography variant="body2" color="text.disabled">--</Typography>
										)}
									</TableCell>
								)}
								<TableCell>
									<Typography variant="body2" color="text.secondary">
										{formatTime(measurement?.timestamp)}
									</Typography>
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
		</TableContainer>
	)
}

function SensorCard({ sensor, onViewDetails, redactionActive, demoWaterLabel }) {
	const measurement = sensor.latestMeasurement
	const alkalinity = measurement?.total_alkalinity_ppm_rolling_avg
	const timestamp = measurement?.timestamp

	const getStatus = () => {
		if (!timestamp) return { label: 'No Data', color: 'default' }
		
		const age = Date.now() - new Date(timestamp).getTime()
		const minutes = age / (1000 * 60)
		
		if (minutes < 5) return { label: 'Live', color: 'success' }
		if (minutes < 60) return { label: 'Recent', color: 'info' }
		return { label: 'Stale', color: 'warning' }
	}

	const status = getStatus()

	const formatValue = (value) => {
		if (value == null) return '--'
		return Number(value).toFixed(1)
	}

	const formatTime = (ts) => {
		if (!ts) return 'No data'
		const date = new Date(ts)
		return date.toLocaleString()
	}

	return (
		<Card
			sx={{
				height: '100%',
				borderTop: `4px solid ${status.color === 'success' ? '#4caf50' : status.color === 'info' ? '#2196f3' : status.color === 'warning' ? '#ff9800' : '#9e9e9e'}`,
				transition: 'all 0.2s',
				cursor: 'pointer',
				'&:hover': {
					transform: 'translateY(-4px)',
					boxShadow: 4
				}
			}}
			onClick={onViewDetails}
		>
			<CardContent sx={{ p: 3 }}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} gap={2}>
					<Box flex={1}>
						<Typography variant="h6" fontWeight="bold" gutterBottom>
							{redactionActive
								? demoWaterLabel(sensor.sensor_id)
								: sensor.sensor_location || 'No location'}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{sensor.sensor_id}
						</Typography>
					</Box>
					<Chip 
						label={status.label} 
						color={status.color} 
						size="small"
						sx={{ flexShrink: 0, mt: 0.45 }}
					/>
				</Box>

				<Box sx={{ my: 3 }}>
					<Typography variant="overline" color="text.secondary" display="block">
						Total Alkalinity
					</Typography>
					<Typography 
						variant="h3" 
						component="div" 
						fontWeight="bold"
						color="primary"
					>
						{formatValue(alkalinity)}
						<Typography 
							component="span" 
							variant="h6" 
							color="text.secondary"
							sx={{ ml: 1 }}
						>
							ppm
						</Typography>
					</Typography>
				</Box>

				<Typography variant="caption" color="text.secondary" display="block" mb={2}>
					Updated: {formatTime(timestamp)}
				</Typography>

				{sensor.show_alkalinity && measurement && (
					<Box display="flex" gap={1} flexWrap="wrap">
						{sensor.show_ph && measurement.ph != null && (
							<Chip 
								label={`pH: ${measurement.ph.toFixed(2)}`}
								size="small"
								variant="outlined"
							/>
						)}
						{sensor.show_temperature && measurement.temperature_c != null && (
							<Chip 
								label={`${measurement.temperature_c.toFixed(1)}°C`}
								size="small"
								variant="outlined"
							/>
						)}
						{sensor.show_conductivity && measurement.conductivity_mscm != null && (
							<Chip 
								label={`${measurement.conductivity_mscm.toFixed(2)} mS/cm`}
								size="small"
								variant="outlined"
							/>
						)}
					</Box>
				)}

				{!sensor.delivery_enabled && (
					<Box mt={2}>
						<Chip 
							label="Delivery Disabled" 
							size="small" 
							color="error"
							variant="outlined"
						/>
					</Box>
				)}
			</CardContent>
		</Card>
	)
}
